import { enhanceFieldVisibility, TGetValueFn } from "@components/smart/FieldInfo";
import {
    EntitySetName,
    EntityTypeName,
    PrCaregivingEventEntity,
    PrCaregivingTypeEntity
} from "@odata/GeneratedEntityTypes";
import { PrCaregivingTypeCode, PrSocialEventTypeCode } from "@odata/GeneratedEnums";
import { getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import CaregivingAllowanceFormView from "@pages/payroll/socialEvents/eventTypes/CaregivingAllowanceFormView";
import { SocialEventGroup } from "@pages/payroll/socialEvents/SocialEvents.types";
import { cloneDeep } from "lodash";

import { IAppContext } from "../../../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType, GroupedField } from "../../../../enums";
import { addFormDef, getFormGroup } from "../../../../views/formView/Form.utils";
import {
    addCaregivingMonthDetailDefinitions,
    addCaregivingReceiverDefinitions,
    addReadOnlyBasedOnDateEventEnd,
    addRequiredActionSummaryDef,
    addSubmissionTableDefinitions,
    addSwitchGroupDefinitions,
    getDefinitions as commonDef,
    getSocialEventsFormDef
} from "../SocialEventsDef";

function isOfCaregivingType(type: PrCaregivingTypeCode): TGetValueFn<boolean> {
    return (args) => {
        const current = args.storage.getValueByPath(PrCaregivingEventEntity.CaregivingType, { useDirectValue: false });
        return current === type;
    };
}

const isSomeoneElseReceivingAllowance: TGetValueFn<boolean> = (args) => {
    return !!args.storage.getValueByPath(PrCaregivingEventEntity.IsSomeoneElseReceivingAllowance);
};

export const getDefinitions: IGetDefinition = (context: IAppContext): IDefinition => {
    const d = cloneDeep(getSocialEventsFormDef(CaregivingAllowanceFormView, PrSocialEventTypeCode.CaregivingAllowance, context));
    const generalGroup = getFormGroup(d, SocialEventGroup.General);

    addRequiredActionSummaryDef(d);
    addReadOnlyBasedOnDateEventEnd(d);

    // add CaregivingType field to general group
    addFormDef(d, PrCaregivingEventEntity.CaregivingType, {
        type: FieldType.ComboBox,
        width: BasicInputSizes.XXL,
        fieldSettings: {
            displayName: PrCaregivingTypeEntity.Name
        },
        affectedFields: [
            { id: PrCaregivingEventEntity.FacilityName },
            { id: PrCaregivingEventEntity.FacilityLegalNumber },
        ]
    });
    addFormDef(d, PrCaregivingEventEntity.FacilityName, {
        width: BasicInputSizes.L,
        groupedField: GroupedField.MultiStart,
        isVisible: isOfCaregivingType(PrCaregivingTypeCode.TheFacilityAttendedByAChildUnder10YearsOldWasClosed),
        isRequired: true
    });
    addFormDef(d, PrCaregivingEventEntity.FacilityLegalNumber, {
        width: BasicInputSizes.S,
        groupedField: GroupedField.MultiEnd,
        isVisible: isOfCaregivingType(PrCaregivingTypeCode.TheFacilityAttendedByAChildUnder10YearsOldWasClosed),
        isRequired: true
    });
    generalGroup.rows.splice(1, 0, [
        { id: PrCaregivingEventEntity.CaregivingType },
        { id: PrCaregivingEventEntity.FacilityName },
        { id: PrCaregivingEventEntity.FacilityLegalNumber },
    ]);

    addCaregivingReceiverDefinitions(d);
    addCaregivingMonthDetailDefinitions(d);

    // add CareEmployeeDeclarations group to definition, including fields
    const yesNoFields = [
        PrCaregivingEventEntity.IsSingleCaregiverOfChild,
        PrCaregivingEventEntity.IsSameHousehold,
        PrCaregivingEventEntity.IsSomeoneElseReceivingAllowance,
        PrCaregivingEventEntity.IsAllowanceReceiverWorking,
        PrCaregivingEventEntity.IsAllowanceReceiverUnfit,
    ];
    addSwitchGroupDefinitions(d, yesNoFields);

    enhanceFieldVisibility(d, PrCaregivingEventEntity.IsAllowanceReceiverWorking, isSomeoneElseReceivingAllowance);
    enhanceFieldVisibility(d, PrCaregivingEventEntity.IsAllowanceReceiverUnfit, isSomeoneElseReceivingAllowance);
    // add affected fields also
    d.form.fieldDefinition[PrCaregivingEventEntity.IsSomeoneElseReceivingAllowance].affectedFields = [
        { id: PrCaregivingEventEntity.IsAllowanceReceiverWorking },
        { id: PrCaregivingEventEntity.IsAllowanceReceiverUnfit },
    ];

    // Table tabs on the end of the form
    addSubmissionTableDefinitions(d);

    return {
        ...d,
        entitySet: EntitySetName.PrCaregivingEvents
    };
};

getDefinitions.translationFiles = [
    ...commonDef.translationFiles,
    getEnumNameSpaceName(EntityTypeName.PrCaregivingType),
    getEnumNameSpaceName(EntityTypeName.PrSocialEventMonthDetailType)
];
setDefByEntityType(EntityTypeName.PrCaregivingEvent, getDefinitions);